import { useEffect, useMemo, useState } from "react";
import { DEFAULT_INPUT, buildProjectBrief, labelProjectType, matchPrograms } from "./lib/matcher";
import type { ImportReport, MatchInput, MatchResult, ProgramRecord, ProgramsPayload } from "./lib/types";

type View = "check" | "programs";
type ProgramSortKey = "title" | "region" | "fundingType" | "aiRelevance";
type SortDirection = "asc" | "desc";
type OfferKey = "report" | "brief" | "partner";
type LeadStatus = "idle" | "saving" | "saved" | "error";
type PaymentLinks = Partial<Record<OfferKey, string>>;
type LeadDetails = {
  email?: string;
  company: string;
  contactName: string;
  phone: string;
  desiredNextStep: string;
  projectContext: string;
};

const PROJECT_TYPES = [
  ["rag", "Wissensassistent / RAG"],
  ["chatbot", "KI-Chatbot"],
  ["automation", "Prozessautomatisierung"],
  ["vision", "Computer Vision"],
  ["forecast", "Datenanalyse / Prognose"],
  ["ecommerce", "Digitaler Vertrieb"]
];

const REGIONS = [
  "Baden-Württemberg",
  "Bayern",
  "Berlin",
  "Brandenburg",
  "Bremen",
  "Hamburg",
  "Hessen",
  "Mecklenburg-Vorpommern",
  "Niedersachsen",
  "Nordrhein-Westfalen",
  "Rheinland-Pfalz",
  "Saarland",
  "Sachsen",
  "Sachsen-Anhalt",
  "Schleswig-Holstein",
  "Thüringen"
];

const PAID_OFFERS: Array<{
  key: OfferKey;
  title: string;
  price: string;
  lead: string;
  cta: string;
  bullets: string[];
}> = [
  {
    key: "report",
    title: "KI-Förderreport",
    price: "79 Euro",
    lead: "Für Unternehmen, die schnell wissen wollen, welche Programme ernsthaft prüfenswert sind.",
    cta: "Report anfordern",
    bullets: ["Top-Programme mit Begründung", "offene Prüfpunkte", "Projektsteckbrief als Grundlage für Beratung"]
  },
  {
    key: "brief",
    title: "Antragsskizze",
    price: "249 Euro",
    lead: "Für Teams, die aus dem Schnellcheck direkt eine brauchbare Projektbeschreibung machen wollen.",
    cta: "Antragsskizze anfragen",
    bullets: ["Zielbild und Arbeitspakete", "förderfähige Kostenlogik", "Textbausteine für Berater oder Förderstelle"]
  },
  {
    key: "partner",
    title: "Partner-Paket",
    price: "ab 490 Euro/Monat",
    lead: "Für KI-Agenturen, Berater und Kanzleien, die Mandanten mit Förderlogik qualifizieren wollen.",
    cta: "Partnerzugang besprechen",
    bullets: ["White-Label-Check", "Mandanten-Reports", "monatliche Programm- und Lead-Auswertung"]
  }
];

const GROWTH_LANES = [
  {
    label: "Direktvertrieb",
    title: "KI-Agenturen und Digitalisierungsberater",
    body: "20 passende Anbieter pro Woche mit einem konkreten Mandanten-Nutzen anschreiben: weniger Förderrecherche, bessere Erstgespräche, qualifizierte Projektsteckbriefe."
  },
  {
    label: "Partner",
    title: "Steuerkanzleien, IHK-nahe Netzwerke, IT-Systemhäuser",
    body: "Einfacher Einstieg: kostenloser Check als Mandantenservice, bezahlter Report oder Partner-Paket für wiederkehrende Anfragen."
  },
  {
    label: "SEO als Hebel",
    title: "Programmseiten und Projektseiten mit Kaufabsicht",
    body: "SEO bleibt wichtig, aber nur als dauerhafter Nachfragekanal. Kurzfristiger Umsatz kommt über direkte Gespräche und Partnerdistribution."
  }
];

const EXAMPLE_REPORT_URL = "/beispielreport/";

export default function App() {
  const [view, setView] = useState<View>(() => {
    if (window.location.hash === "#programme") return "programs";
    return "check";
  });
  const [programs, setPrograms] = useState<ProgramRecord[]>([]);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [input, setInput] = useState<MatchInput>(DEFAULT_INPUT);
  const [leadEmail, setLeadEmail] = useState("");
  const [leadCompany, setLeadCompany] = useState("");
  const [leadContactName, setLeadContactName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadDesiredNextStep, setLeadDesiredNextStep] = useState("report");
  const [leadProjectContext, setLeadProjectContext] = useState("");
  const [leadSaved, setLeadSaved] = useState(false);
  const [leadStatus, setLeadStatus] = useState<LeadStatus>("idle");
  const [paymentLinks, setPaymentLinks] = useState<PaymentLinks>({});
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const applyHashView = () => {
      if (window.location.hash === "#programme") setView("programs");
      if (window.location.hash === "#datenqualitaet") {
        setView("check");
        window.history.replaceState(null, "", "/");
      }
    };
    applyHashView();
    window.addEventListener("hashchange", applyHashView);
    return () => window.removeEventListener("hashchange", applyHashView);
  }, []);

  useEffect(() => {
    fetch("/data/programs.json")
      .then((response) => {
        if (!response.ok) throw new Error(`Daten konnten nicht geladen werden: ${response.status}`);
        return response.json() as Promise<ProgramsPayload>;
      })
      .then((payload) => {
        setPrograms(payload.programs);
        setReport(payload.report);
      })
      .catch((error: Error) => setLoadError(error.message));
  }, []);

  useEffect(() => {
    fetch("/data/payment-links.json")
      .then((response) => (response.ok ? (response.json() as Promise<PaymentLinks>) : {}))
      .then((links) => setPaymentLinks(links))
      .catch(() => setPaymentLinks({}));
  }, []);

  useEffect(() => {
    trackEvent("page_view", { view: window.location.hash === "#programme" ? "programs" : "check" });
  }, []);

  const matches = useMemo(() => matchPrograms(programs, input, 6), [programs, input]);
  const projectBrief = useMemo(() => buildProjectBrief(input, matches), [input, matches]);

  return (
    <div className="app-shell">
      <header className="top-rail">
        <a className="brand" href="/" aria-label="KIProjekte.com Startseite">
          <span className="brand-mark">KP</span>
          <span>KIProjekte.com</span>
        </a>
        <nav aria-label="Hauptnavigation">
          <a href="/ki-foerderung/">KI-Förderung</a>
          <button
            className={view === "check" ? "active" : ""}
            aria-current={view === "check" ? "page" : undefined}
            onClick={() => {
              setView("check");
              window.history.replaceState(null, "", "/");
            }}
          >
            Fördercheck
          </button>
          <button
            className={view === "programs" ? "active" : ""}
            aria-current={view === "programs" ? "page" : undefined}
            onClick={() => {
              setView("programs");
              window.location.hash = "programme";
            }}
          >
            Programme
          </button>
        </nav>
      </header>

      {loadError ? <Status type="error" title="Datenfehler" body={loadError} /> : null}

      {view === "check" ? (
        <CheckSurface
          input={input}
          onInput={setInput}
          matches={matches}
          projectBrief={projectBrief}
          report={report}
          leadEmail={leadEmail}
          leadCompany={leadCompany}
          leadContactName={leadContactName}
          leadPhone={leadPhone}
          leadDesiredNextStep={leadDesiredNextStep}
          leadProjectContext={leadProjectContext}
          leadSaved={leadSaved}
          leadStatus={leadStatus}
          paymentLinks={paymentLinks}
          onLeadEmail={setLeadEmail}
          onLeadCompany={setLeadCompany}
          onLeadContactName={setLeadContactName}
          onLeadPhone={setLeadPhone}
          onLeadDesiredNextStep={setLeadDesiredNextStep}
          onLeadProjectContext={setLeadProjectContext}
          onLeadSave={async () => {
            if (!leadEmail.includes("@")) {
              setLeadStatus("error");
              return;
            }
            setLeadStatus("saving");
            const saved = await saveLead("report", leadEmail, input, matches, projectBrief, {
              company: leadCompany,
              contactName: leadContactName,
              phone: leadPhone,
              desiredNextStep: leadDesiredNextStep,
              projectContext: leadProjectContext
            });
            setLeadSaved(saved);
            setLeadStatus(saved ? "saved" : "error");
          }}
        />
      ) : null}

      {view === "programs" ? <ProgramDirectory programs={programs} report={report} /> : null}
      <SiteFooter />
    </div>
  );
}

function CheckSurface({
  input,
  onInput,
  matches,
  projectBrief,
  report,
  leadEmail,
  leadCompany,
  leadContactName,
  leadPhone,
  leadDesiredNextStep,
  leadProjectContext,
  leadSaved,
  leadStatus,
  paymentLinks,
  onLeadEmail,
  onLeadCompany,
  onLeadContactName,
  onLeadPhone,
  onLeadDesiredNextStep,
  onLeadProjectContext,
  onLeadSave
}: {
  input: MatchInput;
  onInput: (input: MatchInput) => void;
  matches: MatchResult[];
  projectBrief: ReturnType<typeof buildProjectBrief>;
  report: ImportReport | null;
  leadEmail: string;
  leadCompany: string;
  leadContactName: string;
  leadPhone: string;
  leadDesiredNextStep: string;
  leadProjectContext: string;
  leadSaved: boolean;
  leadStatus: LeadStatus;
  paymentLinks: PaymentLinks;
  onLeadEmail: (value: string) => void;
  onLeadCompany: (value: string) => void;
  onLeadContactName: (value: string) => void;
  onLeadPhone: (value: string) => void;
  onLeadDesiredNextStep: (value: string) => void;
  onLeadProjectContext: (value: string) => void;
  onLeadSave: () => void | Promise<void>;
}) {
  const best = matches[0];
  const sourceDate = report ? new Date(report.importedAt).toLocaleString("de-DE") : "Daten werden geladen";
  const nextAction = best?.missing[0] ?? "Projektsteckbrief prüfen und Top-Programm öffnen.";
  const answeredCount = [
    input.projectType,
    input.region,
    input.companySize !== "unknown" ? input.companySize : "",
    input.stage !== "unknown" ? input.stage : "",
    input.budget !== "unknown" ? input.budget : "",
    input.innovationLevel !== "unknown" ? input.innovationLevel : "",
    input.industry,
    input.costTypes.length ? "costs" : ""
  ].filter(Boolean).length;

  return (
    <main>
      <section className="check-intro">
        <div>
          <div className="kicker">Förderlotse für KI-Vorhaben</div>
          <h1>Finden Sie den nächsten Förderpfad für Ihr KI-Projekt.</h1>
          <p>
            Ein ruhiger Schnellcheck für KMU: Projekt skizzieren, Förderlogik verstehen und einen belastbaren
            Projektsteckbrief für den nächsten Beratungstermin vorbereiten.
          </p>
          <div className="intro-actions">
            <a className="primary-link" href="#check-form">Check starten</a>
            <span>Belegt durch Quellenhash. Keine Bewilligungsprognose.</span>
          </div>
        </div>
        <SourceDiagram report={report} best={best} input={input} />
      </section>

      <GuideRail input={input} best={best} report={report} answeredCount={answeredCount} />

      <section className="work-grid">
        <div className="check-panel" id="check-form">
          <div className="panel-head">
            <div>
              <div className="kicker">01 · Projekt erfassen</div>
              <h2>Worum geht es wirklich?</h2>
            </div>
            <p className="source-line">Quellenstand: {sourceDate}. Keine Rechts- oder Förderberatung.</p>
          </div>

          <div className="guided-form" aria-label="Fördercheck Eingaben">
            <section className="input-chapter">
              <div className="chapter-index">A</div>
              <div className="chapter-copy">
                <strong>Projektkern</strong>
                <span>Diese Angaben bestimmen die erste Förderlogik.</span>
              </div>
              <div className="form-grid">
                <SelectField
                  label="KI-Projekttyp"
                  value={input.projectType}
                  onChange={(projectType) => onInput({ ...input, projectType })}
                  options={PROJECT_TYPES}
                  help="Wählen Sie die stärkste Richtung. Details können später ergänzt werden."
                />
                <SelectField
                  label="Unternehmensstandort"
                  value={input.region}
                  onChange={(region) => onInput({ ...input, region })}
                  options={REGIONS.map((region) => [region, region])}
                  help="Bundesprogramme bleiben zusätzlich relevant."
                />
                <TextField
                  label="Branche"
                  value={input.industry}
                  onChange={(industry) => onInput({ ...input, industry })}
                  help="Beispiele: Maschinenbau, Handwerk, Logistik, Gesundheitswirtschaft."
                />
              </div>
            </section>

            <section className="input-chapter">
              <div className="chapter-index">B</div>
              <div className="chapter-copy">
                <strong>Förderfähigkeit</strong>
                <span>Unklare Angaben sind erlaubt und werden im Report markiert.</span>
              </div>
              <div className="form-grid">
                <SelectField
                  label="Unternehmensgröße"
                  value={input.companySize}
                  onChange={(companySize) =>
                    onInput({ ...input, companySize: companySize as MatchInput["companySize"] })
                  }
                  options={[
                    ["kleinstunternehmen", "Kleinstunternehmen"],
                    ["klein", "Klein"],
                    ["mittel", "Mittel"],
                    ["groß", "Groß"],
                    ["unknown", "Weiß ich noch nicht"]
                  ]}
                />
                <SelectField
                  label="Projektphase"
                  value={input.stage}
                  onChange={(stage) => onInput({ ...input, stage: stage as MatchInput["stage"] })}
                  options={[
                    ["idee", "Idee"],
                    ["konzept", "Konzept"],
                    ["pilot", "Pilot"],
                    ["umsetzung", "Umsetzung"],
                    ["unknown", "Weiß ich noch nicht"]
                  ]}
                />
                <SelectField
                  label="Innovationsgrad"
                  value={input.innovationLevel}
                  onChange={(innovationLevel) =>
                    onInput({ ...input, innovationLevel: innovationLevel as MatchInput["innovationLevel"] })
                  }
                  options={[
                    ["niedrig", "eher Standardlösung"],
                    ["mittel", "neu für unser Unternehmen"],
                    ["hoch", "technologisch anspruchsvoll"],
                    ["unknown", "Weiß ich noch nicht"]
                  ]}
                />
              </div>
            </section>

            <section className="input-chapter">
              <div className="chapter-index">C</div>
              <div className="chapter-copy">
                <strong>Kostenrahmen</strong>
                <span>Der Report trennt förderfähige Annahmen von offenen Prüfstellen.</span>
              </div>
              <div className="form-grid">
                <SelectField
                  label="Budgetrahmen"
                  value={input.budget}
                  onChange={(budget) => onInput({ ...input, budget: budget as MatchInput["budget"] })}
                  options={[
                    ["unter-25k", "unter 25.000 Euro"],
                    ["25k-100k", "25.000 bis 100.000 Euro"],
                    ["100k-500k", "100.000 bis 500.000 Euro"],
                    ["500k-plus", "über 500.000 Euro"],
                    ["unknown", "Weiß ich noch nicht"]
                  ]}
                />
                <CostField input={input} onInput={onInput} />
              </div>
            </section>
          </div>
          <div className="check-guidance">
            <div>
              <span>Schnellcheck-Status</span>
              <strong>{answeredCount >= 6 ? "ausreichend für erste Orientierung" : "noch Angaben ergänzen"}</strong>
            </div>
            <p>
              Für einen belastbaren Antrag braucht es danach Angebot, Zeitplan, Kostenaufteilung und technische
              Beschreibung. Diese Punkte landen im Projektsteckbrief.
            </p>
          </div>
        </div>

        <aside className="radar" aria-label="Förderradar">
          <div className="radar-head">
            <span>02 · Förderradar</span>
            <strong>{best ? `${best.score}/100` : "..."}</strong>
          </div>
          {best ? (
            <>
              <div className={`result-summary ${statusType(best.label)}`}>
                <span>{best.label}</span>
                <strong>{best.program.title}</strong>
                <a href={`/foerderprogramme/${best.program.slug}/`}>Programmdetails öffnen</a>
              </div>
              <div className="rule-matrix">
                <div>
                  <span>Projekt</span>
                  <strong>{labelProjectType(input.projectType)}</strong>
                </div>
                <div>
                  <span>Region</span>
                  <strong>{input.region}</strong>
                </div>
                <div>
                  <span>Datenbasis</span>
                  <strong>{report?.selectedPrograms ?? 0} Programme</strong>
                </div>
              </div>
              <h2>Warum dieses Ergebnis?</h2>
              <ul className="compact-list">
                {best.reasons.slice(0, 4).map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
              <h2>Offene Angaben</h2>
              <ul className="compact-list muted">
                {(best.missing.length ? best.missing : ["Keine kritischen Lücken im Schnellcheck."]).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="next-action">
                <span>Nächster sinnvoller Schritt</span>
                <strong>{nextAction}</strong>
                <a href="#report">Zum Projektsteckbrief</a>
              </div>
            </>
          ) : (
            <div className="skeleton">Daten werden geladen.</div>
          )}
        </aside>
      </section>

      <section className="briefing-grid">
        <ResultBriefing matches={matches} />
        <ProjectBriefPanel
          projectBrief={projectBrief}
          input={input}
          matches={matches}
          leadEmail={leadEmail}
          leadCompany={leadCompany}
          leadContactName={leadContactName}
          leadPhone={leadPhone}
          leadDesiredNextStep={leadDesiredNextStep}
          leadProjectContext={leadProjectContext}
          leadSaved={leadSaved}
          leadStatus={leadStatus}
          paymentLinks={paymentLinks}
          onLeadEmail={onLeadEmail}
          onLeadCompany={onLeadCompany}
          onLeadContactName={onLeadContactName}
          onLeadPhone={onLeadPhone}
          onLeadDesiredNextStep={onLeadDesiredNextStep}
          onLeadProjectContext={onLeadProjectContext}
          onLeadSave={onLeadSave}
        />
      </section>

      <RevenueFunnel
        input={input}
        matches={matches}
        projectBrief={projectBrief}
        leadEmail={leadEmail}
        leadCompany={leadCompany}
        leadContactName={leadContactName}
        leadPhone={leadPhone}
        leadDesiredNextStep={leadDesiredNextStep}
        leadProjectContext={leadProjectContext}
        paymentLinks={paymentLinks}
      />
    </main>
  );
}

function SourceDiagram({ report, best, input }: { report: ImportReport | null; best?: MatchResult; input: MatchInput }) {
  return (
    <div className="source-diagram" aria-label="Projektakte und Datenfluss">
      <div className="file-preview">
        <div className="file-label">Projektakte</div>
        <strong>{labelProjectType(input.projectType)}</strong>
        <span>{input.industry} · {input.region}</span>
        <div className="file-lines" aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
      </div>
      <div className="source-pipeline">
        <div className="diagram-node">
          <span>01</span>
          <strong>Amtliche Daten</strong>
          <small>{report ? `${report.totalDocuments.toLocaleString("de-DE")} Dokumente geprüft` : "Import lädt"}</small>
        </div>
        <div className="diagram-node">
          <span>02</span>
          <strong>Belege und Regeln</strong>
          <small>Quellenhash, Nachweise und fachliche Prüfung</small>
        </div>
        <div className="diagram-node accent">
          <span>03</span>
          <strong>{best ? best.label : "Matching"}</strong>
          <small>{best ? best.program.title : `${report?.selectedPrograms ?? 0} Programme im Vergleich`}</small>
        </div>
      </div>
      <p>Orientierung statt Versprechen: Die Plattform zeigt passende Programme, Gründe und offene Prüfstellen.</p>
    </div>
  );
}

function GuideRail({
  input,
  best,
  report,
  answeredCount
}: {
  input: MatchInput;
  best?: MatchResult;
  report: ImportReport | null;
  answeredCount: number;
}) {
  const steps = [
    {
      index: "01",
      title: "Projekt einordnen",
      body: `${answeredCount}/8 Angaben erfasst`,
      state: `${labelProjectType(input.projectType)} · ${input.region}`
    },
    {
      index: "02",
      title: "Passung verstehen",
      body: best ? `${best.label} · ${best.score}/100` : "Matching lädt",
      state: best ? "Regelgründe sichtbar" : "wartet"
    },
    {
      index: "03",
      title: "Report vorbereiten",
      body: report ? `${report.selectedPrograms} Programme im Vergleich` : "Datenbasis lädt",
      state: "Projektsteckbrief prüfen"
    }
  ];

  return (
    <section className="guide-rail" aria-label="Ablauf Fördercheck">
      <div className="progress-track" aria-hidden="true">
        <span style={{ width: `${Math.max(14, (answeredCount / 8) * 100)}%` }} />
      </div>
      {steps.map((step) => (
        <div className="guide-step" key={step.index}>
          <span>{step.index}</span>
          <strong>{step.title}</strong>
          <p>{step.body}</p>
          <small>{step.state}</small>
        </div>
      ))}
    </section>
  );
}

function ResultBriefing({ matches }: { matches: MatchResult[] }) {
  return (
    <section className="module">
      <div className="section-head">
        <div>
          <div className="kicker">Entscheidungsbriefing</div>
          <h2>Top-Programme mit Begründung</h2>
          <p className="module-lead">Die Liste ist priorisiert, nicht endgültig. Öffnen Sie Programme, um Details und Fristen zu prüfen.</p>
        </div>
      </div>
      <div className="match-list">
        {matches.map((match, index) => (
          <article className="match-row" key={match.program.id}>
            <div>
              <span className="match-index">{String(index + 1).padStart(2, "0")}</span>
              <a href={`/foerderprogramme/${match.program.slug}/`}>{match.program.title}</a>
              <p>{match.reasons[0] ?? "Regeln und Quelle prüfen."}</p>
            </div>
            <div className="score">
              <strong>{match.score}</strong>
              <span>{match.label}</span>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function ProjectBriefPanel({
  projectBrief,
  input,
  matches,
  leadEmail,
  leadCompany,
  leadContactName,
  leadPhone,
  leadDesiredNextStep,
  leadProjectContext,
  leadSaved,
  leadStatus,
  paymentLinks,
  onLeadEmail,
  onLeadCompany,
  onLeadContactName,
  onLeadPhone,
  onLeadDesiredNextStep,
  onLeadProjectContext,
  onLeadSave
}: {
  projectBrief: ReturnType<typeof buildProjectBrief>;
  input: MatchInput;
  matches: MatchResult[];
  leadEmail: string;
  leadCompany: string;
  leadContactName: string;
  leadPhone: string;
  leadDesiredNextStep: string;
  leadProjectContext: string;
  leadSaved: boolean;
  leadStatus: LeadStatus;
  paymentLinks: PaymentLinks;
  onLeadEmail: (value: string) => void;
  onLeadCompany: (value: string) => void;
  onLeadContactName: (value: string) => void;
  onLeadPhone: (value: string) => void;
  onLeadDesiredNextStep: (value: string) => void;
  onLeadProjectContext: (value: string) => void;
  onLeadSave: () => void | Promise<void>;
}) {
  const reportHref = buildSalesMailto("report", input, matches, projectBrief, {
    email: leadEmail,
    company: leadCompany,
    contactName: leadContactName,
    phone: leadPhone,
    desiredNextStep: leadDesiredNextStep,
    projectContext: leadProjectContext
  });
  const reportPaymentHref = paymentLinks.report?.trim();
  const leadMessage =
    leadStatus === "saved"
      ? "Gespeichert. Wir melden uns zum nächsten sinnvollen Schritt."
      : leadStatus === "saving"
        ? "Wird gespeichert..."
        : leadStatus === "error"
          ? "Bitte eine gültige E-Mail eintragen. Falls es hakt: Anfrage per E-Mail senden."
          : "Noch kein Versand. Die Anfrage wird erst beim Klick gespeichert.";
  return (
    <section className="module report-module" id="report">
      <div className="kicker">Förderreport Vorschau</div>
      <h2>{projectBrief.title}</h2>
      <p className="module-lead">Nutzen Sie diese Struktur als Gesprächsgrundlage. Offene Punkte werden bewusst sichtbar gelassen.</p>
      <div className="brief-sections">
        {projectBrief.sections.map((section) => (
          <div className={section.missing ? "brief-section missing" : "brief-section"} key={section.title}>
            <span>{section.title}</span>
            <p>{section.body}</p>
          </div>
        ))}
      </div>
      <div className="lead-box">
        <label htmlFor="lead-email">Förderreport mit Projektsteckbrief vorbereiten</label>
        <div className="lead-row">
          <input
            id="lead-email"
            type="email"
            value={leadEmail}
            onChange={(event) => onLeadEmail(event.target.value)}
            placeholder="name@unternehmen.de"
          />
          <button onClick={onLeadSave} disabled={leadStatus === "saving"}>
            {leadStatus === "saving" ? "Speichert..." : "Steckbrief vormerken"}
          </button>
        </div>
        <div className="lead-detail-grid">
          <input
            aria-label="Unternehmen"
            value={leadCompany}
            onChange={(event) => onLeadCompany(event.target.value)}
            placeholder="Unternehmen"
          />
          <input
            aria-label="Kontaktperson"
            value={leadContactName}
            onChange={(event) => onLeadContactName(event.target.value)}
            placeholder="Kontaktperson"
          />
          <input
            aria-label="Telefon"
            value={leadPhone}
            onChange={(event) => onLeadPhone(event.target.value)}
            placeholder="Telefon für Rückfrage"
          />
          <select aria-label="Gewünschter nächster Schritt" value={leadDesiredNextStep} onChange={(event) => onLeadDesiredNextStep(event.target.value)}>
            <option value="report">KI-Förderreport vorbereiten</option>
            <option value="brief">Antragsskizze besprechen</option>
            <option value="partner">Partner-Pilot besprechen</option>
            <option value="callback">Erst kurz Rückruf / Einordnung</option>
          </select>
          <textarea
            aria-label="Projektkontext"
            value={leadProjectContext}
            onChange={(event) => onLeadProjectContext(event.target.value)}
            placeholder="Kurz: Was soll mit KI umgesetzt werden?"
            rows={3}
          />
        </div>
        <p className={leadStatus === "error" ? "lead-error" : ""}>{leadSaved ? leadMessage : leadMessage}</p>
        {reportPaymentHref ? (
          <a className="payment-action" href={reportPaymentHref} onClick={() => trackConversion("report_payment_link")}>
            Report jetzt kaufen
          </a>
        ) : null}
        <a className="secondary-action" href={reportHref} onClick={() => trackConversion("report_mailto")}>
          Report per E-Mail anfragen
        </a>
      </div>
    </section>
  );
}

function RevenueFunnel({
  input,
  matches,
  projectBrief,
  leadEmail,
  leadCompany,
  leadContactName,
  leadPhone,
  leadDesiredNextStep,
  leadProjectContext,
  paymentLinks
}: {
  input: MatchInput;
  matches: MatchResult[];
  projectBrief: ReturnType<typeof buildProjectBrief>;
  leadEmail: string;
  leadCompany: string;
  leadContactName: string;
  leadPhone: string;
  leadDesiredNextStep: string;
  leadProjectContext: string;
  paymentLinks: PaymentLinks;
}) {
  const [offerStatuses, setOfferStatuses] = useState<Record<OfferKey, LeadStatus>>({
    report: "idle",
    brief: "idle",
    partner: "idle"
  });

  const requestInvoiceOffer = async (offerKey: OfferKey) => {
    if (!leadEmail.includes("@")) {
      setOfferStatuses((current) => ({ ...current, [offerKey]: "error" }));
      return;
    }
    setOfferStatuses((current) => ({ ...current, [offerKey]: "saving" }));
    const saved = await saveLead(offerKey, leadEmail, input, matches, projectBrief, {
      company: leadCompany,
      contactName: leadContactName,
      phone: leadPhone,
      desiredNextStep: leadDesiredNextStep,
      projectContext: leadProjectContext
    });
    setOfferStatuses((current) => ({ ...current, [offerKey]: saved ? "saved" : "error" }));
    if (saved) trackConversion(`offer_${offerKey}_invoice_request`);
  };

  return (
    <section className="conversion-section" aria-label="Bezahlte Förderprüfung und Akquiseweg">
      <div className="conversion-head">
        <div>
          <div className="kicker">Nächster Schritt</div>
          <h2>Aus dem Schnellcheck wird ein bezahlbarer Förderreport.</h2>
          <p>
            Die Seite soll nicht auf SEO warten. Der kostenlose Check qualifiziert das Projekt, danach wird der
            konkrete Report, die Antragsskizze oder ein Partnerzugang verkauft.
          </p>
        </div>
        <div className="conversion-proof">
          <span>Funnel-Logik</span>
          <strong>Check → Report → Antragsskizze → Partner</strong>
        </div>
      </div>

      <div className="offer-grid">
        {PAID_OFFERS.map((offer) => (
          <OfferCard
            key={offer.key}
            offer={offer}
            input={input}
            matches={matches}
            projectBrief={projectBrief}
            leadEmail={leadEmail}
            leadCompany={leadCompany}
            leadContactName={leadContactName}
            leadPhone={leadPhone}
            leadDesiredNextStep={leadDesiredNextStep}
            leadProjectContext={leadProjectContext}
            paymentHref={paymentLinks[offer.key]?.trim()}
            status={offerStatuses[offer.key]}
            onInvoiceRequest={() => requestInvoiceOffer(offer.key)}
          />
        ))}
      </div>

      <div className="payment-note">
        <strong>{paymentLinks.report ? "Direktzahlung aktiv" : "Rechnung aktiv, Direktzahlung vorbereitet"}</strong>
        <span>
          {paymentLinks.report
            ? "Stripe-Zahlungslinks sind hinterlegt. Anfragen können direkt bezahlt werden."
            : "Stripe-Zahlungslinks sind technisch vorbereitet. Bis zur Freischaltung können Report, Antragsskizze und Partner-Pilot direkt per Rechnung vorgemerkt werden."}
        </span>
      </div>

      <div className="example-report-cta">
        <div>
          <div className="kicker">Beispielreport</div>
          <h2>Zeigen, was der Kunde bekommt.</h2>
          <p>Der Musterreport macht den bezahlten Output greifbar: Projektsteckbrief, Top-Programme, Begründungen und offene Prüfpunkte.</p>
        </div>
        <a href={EXAMPLE_REPORT_URL} onClick={() => trackConversion("example_report_open")}>
          Beispielreport öffnen
        </a>
      </div>

      <div className="report-preview-grid">
        <article className="module report-sample">
          <div className="kicker">Report-Auszug</div>
          <h2>Was der Kunde bezahlt</h2>
          <div className="sample-block">
            <span>Executive Summary</span>
            <p>
              {labelProjectType(input.projectType)} in {input.region}: {matches[0]?.label ?? "Passung wird geladen"}.
              Der Report priorisiert Programme, trennt Annahmen von Prüfpunkten und liefert eine nutzbare
              Projektbeschreibung.
            </p>
          </div>
          <div className="sample-block">
            <span>Top-Programme</span>
            <ol>
              {matches.slice(0, 3).map((match) => (
                <li key={match.program.id}>
                  <a href={`/foerderprogramme/${match.program.slug}/`}>{match.program.title}</a>
                  <small>{match.score}/100 · {match.label}</small>
                </li>
              ))}
            </ol>
          </div>
        </article>

        <article className="module growth-plan">
          <div className="kicker">Kundengewinnung</div>
          <h2>Nicht SEO-only</h2>
          <div className="growth-lanes">
            {GROWTH_LANES.map((lane) => (
              <div key={lane.label}>
                <span>{lane.label}</span>
                <strong>{lane.title}</strong>
                <p>{lane.body}</p>
              </div>
            ))}
          </div>
        </article>
      </div>
    </section>
  );
}

function OfferCard({
  offer,
  input,
  matches,
  projectBrief,
  leadEmail,
  leadCompany,
  leadContactName,
  leadPhone,
  leadDesiredNextStep,
  leadProjectContext,
  paymentHref,
  status,
  onInvoiceRequest
}: {
  offer: (typeof PAID_OFFERS)[number];
  input: MatchInput;
  matches: MatchResult[];
  projectBrief: ReturnType<typeof buildProjectBrief>;
  leadEmail: string;
  leadCompany: string;
  leadContactName: string;
  leadPhone: string;
  leadDesiredNextStep: string;
  leadProjectContext: string;
  paymentHref?: string;
  status: LeadStatus;
  onInvoiceRequest: () => void | Promise<void>;
}) {
  const href =
    paymentHref ||
    buildSalesMailto(offer.key, input, matches, projectBrief, {
      email: leadEmail,
      company: leadCompany,
      contactName: leadContactName,
      phone: leadPhone,
      desiredNextStep: leadDesiredNextStep,
      projectContext: leadProjectContext
    });
  const statusMessage =
    status === "saved"
      ? "Vorgemerkt. Wir melden uns mit Rechnung und nächstem Schritt."
      : status === "saving"
        ? "Wird gespeichert..."
        : status === "error"
          ? "Bitte oben eine gültige E-Mail eintragen oder per E-Mail anfragen."
          : "Ohne Stripe-Link als Rechnungsauftrag vormerken.";
  return (
    <article className="offer-card">
      <div>
        <span>{offer.title}</span>
        <strong>{offer.price}</strong>
        <p>{offer.lead}</p>
      </div>
      <ul>
        {offer.bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
      {paymentHref ? (
        <a href={href} onClick={() => trackConversion(`offer_${offer.key}_payment_link`)}>
          Jetzt kaufen
        </a>
      ) : (
        <div className="offer-actions">
          <button onClick={onInvoiceRequest} disabled={status === "saving"}>
            {status === "saving" ? "Speichert..." : "Per Rechnung beauftragen"}
          </button>
          <a href={href} onClick={() => trackConversion(`offer_${offer.key}_mailto`)}>
            Per E-Mail anfragen
          </a>
          <p className={status === "error" ? "offer-note error" : "offer-note"}>{statusMessage}</p>
        </div>
      )}
    </article>
  );
}

function buildSalesMailto(
  offer: OfferKey,
  input: MatchInput,
  matches: MatchResult[],
  projectBrief: ReturnType<typeof buildProjectBrief>,
  leadDetails: LeadDetails
) {
  const selectedOffer = PAID_OFFERS.find((item) => item.key === offer) ?? PAID_OFFERS[0];
  const best = matches[0];
  const subject = `${selectedOffer.title} für ${labelProjectType(input.projectType)}`;
  const body = [
    "Hallo KIProjekte.com,",
    "",
    `ich interessiere mich für: ${selectedOffer.title} (${selectedOffer.price})`,
    leadDetails.email ? `Meine E-Mail: ${leadDetails.email}` : "",
    leadDetails.company ? `Unternehmen: ${leadDetails.company}` : "",
    leadDetails.contactName ? `Kontaktperson: ${leadDetails.contactName}` : "",
    leadDetails.phone ? `Telefon: ${leadDetails.phone}` : "",
    leadDetails.desiredNextStep ? `Gewünschter nächster Schritt: ${labelDesiredNextStep(leadDetails.desiredNextStep)}` : "",
    leadDetails.projectContext ? `Projektkontext: ${leadDetails.projectContext}` : "",
    "",
    "Projektkontext:",
    `- Projekttyp: ${labelProjectType(input.projectType)}`,
    `- Standort: ${input.region}`,
    `- Branche: ${input.industry || "noch offen"}`,
    `- Unternehmensgröße: ${input.companySize}`,
    `- Phase: ${input.stage}`,
    `- Budget: ${input.budget}`,
    `- Kostenarten: ${input.costTypes.join(", ") || "noch offen"}`,
    "",
    "Aktueller Top-Treffer:",
    best ? `- ${best.program.title} (${best.score}/100, ${best.label})` : "- noch kein Treffer geladen",
    "",
    "Projektsteckbrief:",
    ...projectBrief.sections.map((section) => `- ${section.title}: ${section.body}`),
    "",
    "Bitte melden Sie sich mit dem nächsten sinnvollen Schritt.",
    ""
  ].filter(Boolean);

  return `mailto:info@kiprojekte.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body.join("\n"))}`;
}

function getSourcePath() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function getAttribution() {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get("utm_source") ?? "",
    utmMedium: params.get("utm_medium") ?? "",
    utmCampaign: params.get("utm_campaign") ?? "",
    utmContent: params.get("utm_content") ?? ""
  };
}

async function saveLead(
  offerKey: OfferKey,
  email: string,
  input: MatchInput,
  matches: MatchResult[],
  projectBrief: ReturnType<typeof buildProjectBrief>,
  leadDetails: LeadDetails
) {
  const best = matches[0];
  const projectSummary = [
    leadDetails.contactName ? `Kontaktperson: ${leadDetails.contactName}` : "",
    leadDetails.phone ? `Telefon: ${leadDetails.phone}` : "",
    leadDetails.desiredNextStep ? `Gewünschter nächster Schritt: ${labelDesiredNextStep(leadDetails.desiredNextStep)}` : "",
    leadDetails.projectContext ? `Projektkontext: ${leadDetails.projectContext}` : "",
    ...projectBrief.sections.map((section) => `${section.title}: ${section.body}`)
  ]
    .filter(Boolean)
    .join("\n");
  try {
    const response = await fetch("/api/leads", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        email,
        company: leadDetails.company,
        offerKey,
        projectSummary,
        sourcePath: getSourcePath(),
        metadata: {
          attribution: getAttribution(),
          contact: {
            name: leadDetails.contactName,
            phone: leadDetails.phone,
            desiredNextStep: leadDetails.desiredNextStep
          },
          input,
          topPrograms: matches.slice(0, 3).map((match) => ({
            slug: match.program.slug,
            title: match.program.title,
            score: match.score,
            label: match.label
          })),
          bestProgram: best?.program.slug ?? null
        }
      })
    });
    if (!response.ok) return false;
    const payload = (await response.json()) as { ok?: boolean };
    return Boolean(payload.ok);
  } catch {
    return false;
  }
}

function labelDesiredNextStep(value: string) {
  if (value === "brief") return "Antragsskizze besprechen";
  if (value === "partner") return "Partner-Pilot besprechen";
  if (value === "callback") return "Erst kurz Rückruf / Einordnung";
  return "KI-Förderreport vorbereiten";
}

function trackConversion(eventName: string) {
  const payload = { event: eventName, page: getSourcePath(), at: new Date().toISOString() };
  const dataLayer = (window as unknown as { dataLayer?: Array<Record<string, string>> }).dataLayer;
  dataLayer?.push(payload);
  window.localStorage.setItem("kiprojekte:lastConversion", JSON.stringify(payload));
  trackEvent(eventName);
}

function trackEvent(eventName: string, metadata: Record<string, unknown> = {}) {
  const body = JSON.stringify({
    event: eventName,
    page: window.location.pathname,
    sourcePath: getSourcePath(),
    metadata: {
      ...metadata,
      attribution: getAttribution()
    }
  });

  if (navigator.sendBeacon) {
    navigator.sendBeacon("/api/events", new Blob([body], { type: "application/json" }));
    return;
  }

  fetch("/api/events", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true
  }).catch(() => undefined);
}

function ProgramDirectory({ programs, report }: { programs: ProgramRecord[]; report: ImportReport | null }) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<ProgramSortKey>("aiRelevance");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const normalizedQuery = query.trim().toLowerCase();
  const visiblePrograms = useMemo(() => {
    const filtered = normalizedQuery
      ? programs.filter((program) =>
          [
            program.title,
            program.region,
            program.level,
            program.fundingType.join(" "),
            program.eligibleApplicants.join(" "),
            program.eligibleCosts.join(" "),
            program.keywords.join(" ")
          ]
            .join(" ")
            .toLowerCase()
            .includes(normalizedQuery)
        )
      : programs;
    return [...filtered].sort((a, b) => {
      const direction = sortDirection === "asc" ? 1 : -1;
      if (sortKey === "aiRelevance") return (a.aiRelevance - b.aiRelevance) * direction;
      const aValue = sortKey === "fundingType" ? a.fundingType.join(", ") : String(a[sortKey]);
      const bValue = sortKey === "fundingType" ? b.fundingType.join(", ") : String(b[sortKey]);
      return aValue.localeCompare(bValue, "de", { sensitivity: "base" }) * direction;
    });
  }, [normalizedQuery, programs, sortDirection, sortKey]);

  const changeSort = (key: ProgramSortKey) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      return;
    }
    setSortKey(key);
    setSortDirection(key === "aiRelevance" ? "desc" : "asc");
  };

  const sortLabel = (key: ProgramSortKey) => (sortKey === key ? (sortDirection === "asc" ? " aufsteigend" : " absteigend") : "");

  return (
    <main id="programme">
      <section className="directory-head">
        <div>
          <div className="kicker">Programmverzeichnis</div>
          <h1>{programs.length} relevante Förderprogramme</h1>
          <p className="lead">
            Aus 2.501 amtlichen Dokumenten wurden Programme mit KI-, Digitalisierungs- und Innovationssignalen
            priorisiert. Jede Detailseite zeigt Quellenstand, Förderlogik und einen direkten Prüfpfad.
          </p>
        </div>
        <Status
          type="info"
          title="Importstatus"
          body={report ? `${report.totalDocuments} Dokumente geprüft, ${report.selectedPrograms} Programme ausgewählt.` : "Daten werden geladen."}
        />
      </section>
      <section className="directory-toolbar" aria-label="Programme filtern">
        <label className="search-field" htmlFor="program-search">
          <span>Suche</span>
          <input
            id="program-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Programm, Gebiet, Förderart oder Signal"
          />
        </label>
        <p>
          {visiblePrograms.length} von {programs.length} Programmen sichtbar. Sortierung:{" "}
          {sortKey === "aiRelevance" ? "Score" : sortKey === "fundingType" ? "Förderart" : sortKey === "region" ? "Gebiet" : "Programm"}
          {sortDirection === "asc" ? " aufsteigend" : " absteigend"}.
        </p>
      </section>
      <div className="program-table" aria-label="Förderprogramme">
        <div className="program-table-head">
          <button onClick={() => changeSort("title")} aria-label={`Nach Programm sortieren${sortLabel("title")}`}>
            Programm
          </button>
          <button onClick={() => changeSort("region")} aria-label={`Nach Gebiet sortieren${sortLabel("region")}`}>
            Gebiet
          </button>
          <button onClick={() => changeSort("fundingType")} aria-label={`Nach Förderart sortieren${sortLabel("fundingType")}`}>
            Förderart
          </button>
          <button onClick={() => changeSort("aiRelevance")} aria-label={`Nach Score sortieren${sortLabel("aiRelevance")}`}>
            Score
          </button>
        </div>
        {visiblePrograms.map((program) => (
          <a className="program-row" href={`/foerderprogramme/${program.slug}/`} key={program.id}>
            <span>{program.title}</span>
            <span>{program.region}</span>
            <span>{program.fundingType.join(", ")}</span>
            <span>{program.aiRelevance}</span>
          </a>
        ))}
        {visiblePrograms.length === 0 ? (
          <div className="program-empty">Keine Programme für diesen Suchbegriff gefunden.</div>
        ) : null}
      </div>
    </main>
  );
}

function Status({ type, title, body }: { type: "success" | "warning" | "error" | "info"; title: string; body: string }) {
  return (
    <div className={`status ${type}`}>
      <span>{title}</span>
      <strong>{body}</strong>
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
  help
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[][] | Array<[string, string]>;
  help?: string;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map(([optionValue, optionLabel]) => (
          <option value={optionValue} key={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
      {help ? <small>{help}</small> : null}
    </label>
  );
}

function TextField({ label, value, onChange, help }: { label: string; value: string; onChange: (value: string) => void; help?: string }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} />
      {help ? <small>{help}</small> : null}
    </label>
  );
}

function CostField({ input, onInput }: { input: MatchInput; onInput: (input: MatchInput) => void }) {
  const costs = [
    ["software", "Software & Lizenzen"],
    ["beratung", "Beratung"],
    ["hardware", "Hardware"],
    ["daten", "Daten-\u00adaufbereitung"],
    ["personal", "Personal"]
  ];
  return (
    <fieldset className="field cost-field">
      <legend>Kostenarten</legend>
      <div>
        {costs.map(([cost, label]) => {
          const checked = input.costTypes.includes(cost);
          return (
            <label key={cost}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() =>
                  onInput({
                    ...input,
                    costTypes: checked ? input.costTypes.filter((item) => item !== cost) : [...input.costTypes, cost]
                  })
                }
              />
              <span>{label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function statusType(label: string): "success" | "warning" | "error" | "info" {
  if (label.includes("wahrscheinlich")) return "success";
  if (label.includes("möglicherweise") || label.includes("unklar")) return "warning";
  if (label.includes("unpassend")) return "error";
  return "info";
}

function SiteFooter() {
  return (
    <footer className="site-footer">
      <strong>KIProjekte.com</strong>
      <nav aria-label="Rechtliches und Kontakt">
        <a href="/ki-foerderung/">KI-Förderung</a>
        <a href="/ki-foerderreport/">Förderreport</a>
        <a href="/beispielreport/">Beispielreport</a>
        <a href="/partner/">Partner</a>
        <a href="/impressum/">Impressum</a>
        <a href="/datenschutz/">Datenschutz</a>
        <a href="/kontakt/">Kontakt</a>
      </nav>
    </footer>
  );
}
